import { useState } from "react";
import { UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";

const emptyPerson = { name: "", email: "", password: "" };

export default function PersonBuilder({
  endpoint,
  personLabel,
  testPrefix,
  onCreated,
  assignmentOptions = [],
  assignmentLabel,
  assignmentField,
  multiple = false,
  secondaryOptions = [],
  secondaryLabel,
  secondaryField,
}) {
  const [person, setPerson] = useState(emptyPerson);
  const [assignment, setAssignment] = useState(multiple ? [] : "");
  const [secondaryAssignment, setSecondaryAssignment] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (field, value) => setPerson({ ...person, [field]: value });

  const submit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...person,
        ...(assignmentField ? { [assignmentField]: assignment } : {}),
        ...(secondaryField ? { [secondaryField]: secondaryAssignment } : {}),
      };
      const response = await api.post(endpoint, payload);
      setPerson(emptyPerson);
      setAssignment(multiple ? [] : "");
      setSecondaryAssignment("");
      toast.success(`${personLabel} account created`);
      onCreated(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className="person-builder" onSubmit={submit} data-testid={`${testPrefix}-form`}>
      <div className="builder-heading">
        <p className="eyebrow">New {personLabel.toLowerCase()} account</p>
        <h3>Create a secure {personLabel.toLowerCase()} login</h3>
      </div>
      <label htmlFor={`${testPrefix}-name`}>Full name</label>
      <input
        id={`${testPrefix}-name`}
        value={person.name}
        onChange={(event) => updateField("name", event.target.value)}
        required
        data-testid={`${testPrefix}-name-input`}
      />
      <label htmlFor={`${testPrefix}-email`}>Email address</label>
      <input
        id={`${testPrefix}-email`}
        type="email"
        value={person.email}
        onChange={(event) => updateField("email", event.target.value)}
        required
        data-testid={`${testPrefix}-email-input`}
      />
      <label htmlFor={`${testPrefix}-password`}>Initial password</label>
      <input
        id={`${testPrefix}-password`}
        type="password"
        value={person.password}
        onChange={(event) => updateField("password", event.target.value)}
        minLength="8"
        autoComplete="new-password"
        required
        data-testid={`${testPrefix}-password-input`}
      />
      {assignmentField && (
        <>
          <label htmlFor={`${testPrefix}-assignment`}>{assignmentLabel}</label>
          <select
            id={`${testPrefix}-assignment`}
            multiple={multiple}
            value={assignment}
            onChange={(event) => {
              const value = multiple
                ? Array.from(event.target.selectedOptions).map((option) => option.value)
                : event.target.value;
              setAssignment(value);
            }}
            required
            data-testid={`${testPrefix}-assignment-select`}
          >
            {!multiple && <option value="">Select an option</option>}
            {assignmentOptions.map((option) => (
              <option value={option.id} key={option.id}>{option.label}</option>
            ))}
          </select>
        </>
      )}
      {secondaryField && (
        <>
          <label htmlFor={`${testPrefix}-secondary-assignment`}>{secondaryLabel}</label>
          <select
            id={`${testPrefix}-secondary-assignment`}
            value={secondaryAssignment}
            onChange={(event) => setSecondaryAssignment(event.target.value)}
            required
            data-testid={`${testPrefix}-secondary-assignment-select`}
          >
            <option value="">Select an option</option>
            {secondaryOptions.map((option) => <option value={option.id} key={option.id}>{option.label}</option>)}
          </select>
        </>
      )}
      <button className="primary-button" disabled={isSaving} data-testid={`${testPrefix}-submit-button`}>
        <UserPlus aria-hidden="true" size={19} />
        {isSaving ? "Creating" : `Create ${personLabel}`}
      </button>
    </form>
  );
}